// ABOUTME: Chip input component for managing child nicknames/aliases
// ABOUTME: Used for Alexa voice recognition - validates max 10 nicknames, 50 chars each

import React, { useState, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  FormHelperText,
  Box
} from '@mui/material';

interface NicknamesChipInputProps {
  value: string[];
  onChange: (nicknames: string[]) => void;
  childName: string;
  disabled?: boolean;
}

const MAX_NICKNAMES = 10;
const MAX_NICKNAME_LENGTH = 50;

const NicknamesChipInput: React.FC<NicknamesChipInputProps> = ({
  value,
  onChange,
  childName,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Track when we just set an error to prevent immediate clearing by input change events
  const justSetErrorRef = useRef(false);
  // Track when validation failed so Autocomplete doesn't proceed
  const validationFailedRef = useRef(false);

  const validateNickname = (nickname: string): string | null => {
    const trimmed = nickname.trim();

    if (!trimmed) {
      return null; // Empty is not an error, just don't add
    }

    if (trimmed.length > MAX_NICKNAME_LENGTH) {
      return 'Nickname must be 50 characters or less';
    }

    if (value.length >= MAX_NICKNAMES) {
      return 'Maximum 10 nicknames allowed';
    }

    if (trimmed.toLowerCase() === childName.toLowerCase()) {
      return 'Same as primary name';
    }

    if (value.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      return 'Nickname already exists';
    }

    return null;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const trimmed = inputValue.trim();
      // If empty/whitespace only, prevent adding
      if (!trimmed) {
        event.preventDefault();
        event.stopPropagation();
        validationFailedRef.current = true;
        return;
      }

      const validationError = validateNickname(inputValue);
      if (validationError) {
        event.preventDefault();
        event.stopPropagation();
        setError(validationError);
        justSetErrorRef.current = true;
        validationFailedRef.current = true;
      } else {
        validationFailedRef.current = false;
      }
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    // If validation failed in onKeyDown, don't proceed
    if (validationFailedRef.current) {
      validationFailedRef.current = false;
      return;
    }

    // Check if adding a new value (secondary validation in case Enter wasn't intercepted)
    if (newValue.length > value.length) {
      const addedValue = newValue[newValue.length - 1];
      const validationError = validateNickname(addedValue);
      if (validationError) {
        setError(validationError);
        justSetErrorRef.current = true;
        return;
      }
    }
    setError(null);
    onChange(newValue.map(v => v.trim()).filter(v => v));
  };

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    // Only clear error if user is typing new content (not when Autocomplete clears after Enter)
    if (error && newInputValue.length > 0) {
      setError(null);
      justSetErrorRef.current = false;
    } else if (newInputValue.length === 0 && justSetErrorRef.current) {
      // Input was cleared after validation error, keep the error displayed
      justSetErrorRef.current = false;
    } else if (error && newInputValue.length === 0 && !justSetErrorRef.current) {
      // Normal clear (user deleted all text)
      setError(null);
    }
  };

  const handleBlur = () => {
    // Auto-add any pending text when focus leaves the input (e.g., clicking Save)
    const trimmed = inputValue.trim();
    if (trimmed) {
      const validationError = validateNickname(trimmed);
      if (validationError) {
        setError(validationError);
        justSetErrorRef.current = true;
      } else {
        onChange([...value, trimmed]);
        setInputValue('');
        setError(null);
      }
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={value}
        inputValue={inputValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        disabled={disabled}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option}
              {...getTagProps({ index })}
              key={option}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Nicknames"
            placeholder={value.length === 0 ? "Type and press Enter" : ""}
            error={!!error}
            helperText={error}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Enter nicknames for Alexa voice recognition',
            }}
          />
        )}
      />
      <FormHelperText sx={{ ml: 1.5 }}>
        Add nicknames that Alexa can recognize (e.g., &quot;Em&quot; or &quot;Emmy&quot; for &quot;Emma&quot;)
      </FormHelperText>
    </Box>
  );
};

export default NicknamesChipInput;
